import React, { useState, useMemo, useRef } from 'react';
import clsx from 'clsx';
import produce from 'immer';
import { useTranslation } from 'react-i18next';
import { Node, Transforms } from 'slate';
import { useSlate } from 'slate-react';
import { useMutation } from 'react-query';
import { toast } from 'components/Shared/Toast';
import { TabContent, TabPane, Nav, NavItem, NavLink, Form, FormGroup, Input, Collapse, CustomInput,  Card, Button, CardTitle, CardText, Row, Col } from 'reactstrap';
import styled from 'styled-components';

import bootbox from 'components/Modals/Bootbox';
import { truncate, format, extractHTMLContent } from 'utils'
import { getNodesById } from '../../Canvas/TratoFormat';
import { useEditorState, useEditorActions } from '../../editor-context'
import { useProfileState, useProfileAbility } from 'context/profile'
import { createReviewComment, respondReviewComment, deleteReviewComment, respondParticipantComment, createConversationComment, respondConversationComment, respondSignComment } from 'api/actions/collaboration'
import { fetcher } from 'api/client'


const StyledInput = styled(Input)`
  width: 85% !important;
`
const StyledNav = styled(Nav)`
  height: 40px;
`
const StyledTabContent = styled(TabContent)`
  height: ${props => props.hasTitle ? 'calc(100% - 140px)' : 'calc(100% - 40px)'};
  .tab-pane.active {
    display: flex;
  }
`
const CommentTitle = styled.div`
  height: 100px;
`

export function Comments() {
  const { t } = useTranslation();
  const { document: doc,  currentElement, mode, documentType } = useEditorState()
  const editor = useSlate()
  const { setCurrentElement, updateDocument } = useEditorActions()
  const { profile } = useProfileState()
  const messageRef = useRef()
  const profileAbility = useProfileAbility();
  const canViewCollaborationComments = profileAbility.can('VIEW_COLLABORATION_COMMENTS'); // can VIEW_COLLABORATION_COMMENTS
  const [currentTab, setCurrentTab] = useState(canViewCollaborationComments ? 'collab' : 'participant')

  const onError = (error) => {
    console.log({error})
    toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
  } 

  const updateElement = (editor, elementId, element) => {  
    const elementLocations = getNodesById(editor, elementId)

    elementLocations.map(([node, location]) => {
        Transforms.setNodes(editor, element, {
            mode: 'all',
            at: location,   
            voids: true
        })
    })
  }

  const { mutate: createReviewCommentMutate } = useMutation((payload) => fetcher(createReviewComment(payload)), {
    onSuccess: (data) => {
      const updatedElement = produce(currentElement, (draft => {
        if (!draft.reviewComments) draft.reviewComments = []
        draft.reviewComments.push(data)
      })) 

      setCurrentElement({ element: updatedElement, location: currentElement.editorLocation })
      updateElement(editor, updatedElement._id, updatedElement)
    },
    onError
  })

  const { mutate: createConversationMutate } = useMutation((payload) => fetcher(createConversationComment(payload)), {
    onSuccess: (data) => {
      updateDocument({
        conversations: [
          ...doc.conversations,
          data
        ]
      })
    },
    onError
  })

  const { mutate: respondCommentMutate } = useMutation((payload) => fetcher(currentTab === 'collab' ? respondReviewComment(payload) : respondParticipantComment(payload)), {
    onSuccess: (data, variables) => {
      const updatedElement = produce(currentElement, (draft => {
        const comments = currentTab === 'collab' ? draft.reviewComments : draft.signComments;
        const commentIdx = comments.findIndex(c => c._id === variables.commentId)
        comments[commentIdx].answers.push(currentTab === 'collab' ? data : variables.newComment);
      })) 

      setCurrentElement({ element: updatedElement, location: currentElement.editorLocation })
      updateElement(editor, updatedElement._id, updatedElement)
    },
    onError
  })

  const { mutate: respondConversationMutate } = useMutation((payload) => fetcher(currentTab === 'collab' ? respondConversationComment(payload) : respondSignComment(payload)), {
    onSuccess: (data, variables) => {
      const commentsKey = currentTab === 'collab' ? 'conversations' : 'comments';
      const conversations = produce(doc[commentsKey], (draft => {
        const commentIdx = draft.findIndex(c => c._id === variables.commentId)
        if (!draft[commentIdx].answers) draft[commentIdx].answers = []
        console.log({})
        draft[commentIdx].answers.push(data.answer || variables.answer || data);
      })) 
      updateDocument({
        [commentsKey]: conversations
      })
    
    },
    onError
  })

  const { mutate: deleteReviewCommentMutate } = useMutation((payload) => fetcher(deleteReviewComment(payload)), {
    onSuccess: (data, variables) => {
      const updatedElement = produce(currentElement, (draft => {
        draft.reviewComments = draft.reviewComments.filter(c => c._id !== variables.commentId)
      })) 

      setCurrentElement({ element: updatedElement, location: currentElement.editorLocation })
      updateElement(editor, updatedElement._id, updatedElement)
    },
    onError
  })

  const isElementSaved = !currentElement.isNew;
  const isElementSelected = useMemo(() => !!currentElement._id, [currentElement._id])
  const isOwner = profileAbility.can('modify', doc)

  const content = useMemo(() => {
      if (!isElementSelected) return ''
      if (!doc.isMigrated) return extractHTMLContent(currentElement.content);
      return Node.string(currentElement) || ''
  }, [currentElement, isElementSelected])

  const changeTab = (tab) => () => {
    
    if (!canViewCollaborationComments) {
      setCurrentTab('participant')
      return;
    }
    setCurrentTab(tab)
  }

  const handleSendMessage = () => {
		const message = messageRef.current.value;

		if (message.length > 0) {
			const elementContent = content.substring(0, 100) + '...';
      const newComment = {
        contractId: doc._id,
        documentType: mode,
        elementId: currentElement._id,
        answers: [], 
        message, 
        createdAt: new Date(),
        user: profile._id, 
        element: { item: currentElement.type, content: elementContent }
      }
    
      if (documentType === 'trato') {
        createReviewCommentMutate(newComment)
      } else  {
        createConversationMutate(newComment)
      }
      
      messageRef.current.value = '';
		}
  }

  const handleSendResponse = ({ comment, message }) => {
    const answer = { 
      message: message, 
      user: profile, 
      email: profile.email,
      createdAt: new Date() 
    }
    

    const updatedComment = produce(comment, (draft => {
      if (!draft.answers) draft.answers = [];
      draft.answers.push(answer)
    })) 

		if (message.length > 0) {
      const payload = {
        ...updatedComment,
        answer,
        message,
        newComment: answer,
        contractId: doc._id,
        commentId: comment._id,
        documentType: mode,
        elementId: currentElement._id,
      }
      console.log({comment, payload})

      if (documentType === 'trato') {
        respondCommentMutate(payload)
      } else  {
        payload.message = updatedComment.message;
        respondConversationMutate(payload)
      }
		}
  }

  const handleDelete = async (commentId) => {
    const resp = await bootbox.confirm(t('editor.comments.delete_confirm'));
    if (!resp) return;

    deleteReviewCommentMutate({
      contractId: doc._id,
      documentType: mode,
      elementId: currentElement._id,
      commentId
    })
  }

  if (!isElementSelected && documentType === 'trato') {
    return (
      <div className="sidebar-content">
        <small className="empty-model d-block text-center text-dark p-3">
          {t('editor.comments.empty')}
        </small> 
      </div>
    )
  }

  return (
    <div className="sidebar-content h-100">
        {
          documentType === 'trato' && 
            <CommentTitle className="comment-title text-dark text-center p-3 d-flex flex-column bg-light-light">
              <strong className="m-b">{t('editor.comments.related')}:</strong>{' '}
              <i className="comment-content text-bold">{truncate(content, 90)}</i>
            </CommentTitle>
        }        
          <StyledNav className="bg-light border-bottom" tabs fill>
              <NavItem>
                <NavLink
                  className={clsx('border-light', currentTab === 'collab' && 'active', !canViewCollaborationComments && 'not-allowed')}
                  title={!canViewCollaborationComments ? t('wordList.plans.availablePE') : ''} 
                  onClick={changeTab('collab')}
                >
                  {mode === 'contract' && t('editor.comments.collaboration')}
                </NavLink>
              </NavItem>
              
                {/* documentType === 'trato' && */}
                <NavItem>
                  <NavLink
                    className={clsx('border-light', currentTab === 'participant' && 'active')} 
                    onClick={changeTab('participant')}
                  >
                    {t('editor.comments.participants')}
                  </NavLink>
                </NavItem>
              {/* } */}
          </StyledNav>
          <StyledTabContent hasTitle={documentType === 'trato'} activeTab={currentTab}>
            <TabPane  className={'h-100 flex-column justify-content-between'}  role="tabpanel" tabId="collab" id="comment-collab" hidden={!canViewCollaborationComments}>
            {/* <div className="h-100 > */}
              <div className="comment-collab-listing">
                  <CommentList isTratoFormat={documentType === 'trato'} list={documentType === 'trato' ? currentElement.reviewComments : doc.conversations} isOwner={isOwner} canDelete={documentType === 'trato'} onDelete={handleDelete} currentTab={currentTab} onRespond={handleSendResponse} />
              </div>
              <div className="comment-collab-send p-2">
                <Form inline>
                  <FormGroup className="w-100">
                    <StyledInput 
                      type="textarea" 
                      name="email" 
                      size="sm"
                      title={!isElementSaved ? t('errors.saveFirst') : ''}
                      innerRef={messageRef}
                      name="new-message-content" 
                      placeholder={!isElementSaved ? t('errors.saveFirst') : t('editor.comments.new_placeholder')}
                    />
                    <Button className="ml-1" type="button" size="lg" color="success" data-action="send-new-message" disabled={!isElementSaved} onClick={handleSendMessage}>
                      <i className="fas fa-paper-plane"></i>
                    </Button>
                  </FormGroup>
                </Form>
              </div>
              {/* </div> */}
            </TabPane>
            <TabPane role="tabpanel" tabId="participant" id="comment-participant">
                <CommentList 
                  isTratoFormat={documentType === 'trato'} 
                  list={documentType === 'trato' ? currentElement.signComments : doc.comments} 
                  isOwner={isOwner} 
                  currentTab={currentTab} 
                  onDelete={handleDelete} 
                  onRespond={handleSendResponse} 
                />
            </TabPane>
          </StyledTabContent>
      </div>


  )
}

const getUsernameOrEmail = (user = {}, email) => {
  return user?.givenName ? `${user.givenName} ${user.familyName || ''}` : email 
}

const getPhoto = (user = {}) => {
  if (!user || !user.photo) return '/images/buho-avatar.png'

  return user.photo;
}

function CommentList({ currentTab, list = [], canDelete, isOwner, isTratoFormat, onDelete, onRespond }) {
  const { t } = useTranslation();

  if (!list.length) {
    return (
      <div className="empty-comments text-dark p-2 text-center w-100">
        {isTratoFormat ? t('editor.comments.elem_empty') : t('sign.contract.comments_pdf_empty')}
      </div>
    )
  }

  return(
    <div className="comment-container">
      { 
        list.map(comment => (
          <CommentItemContainer key={comment._id} comment={comment} currentTab={currentTab} isOwner={isOwner} canDelete={canDelete} onDelete={onDelete} onRespond={onRespond} />
        ))
      }
    </div>
    )
}

function CommentItemContainer({ currentTab, comment, isOwner, canDelete, onDelete, onRespond }) {
  const { t } = useTranslation();
  const messageRef = useRef()
  const [isExpanded, setIsExpanded] = useState(true);
  const { user, email, answers = [], message, createdAt } = comment;

  const handleDelete = () => {
    onDelete(comment._id)
  }

  const handleSend = () => {
    onRespond({
      comment,
      message: messageRef.current.value
    })
    messageRef.current.value = '';
  }

  const toggle = () => setIsExpanded(!isExpanded);

  return (
    <CommentItem 
      user={user}
      email={email}
      message={message}
      createdAt={createdAt}
      isOwner={isOwner}
      currentTab={currentTab}
      canDelete={canDelete}
      onDelete={handleDelete}
    >
      {!!answers.length &&
        <div className="mb-2">
          <a className="show-answers active text-dark" onClick={toggle}>
            <small className="text-active">
              {isExpanded ? t('editor.comments.hide_answers') : t('editor.comments.show_answers')}
            </small>
          </a>
        </div>
      }
      <Collapse isOpen={isExpanded}>
          {answers.map((answer) => (
            <CommentItem 
              key={answer._id}
              user={answer.user}
              email={answer.email}
              createdAt={answer.createdAt}
              message={answer.message}
            />
          ))}
      </Collapse>
      <Form inline>
        <FormGroup className="form-group">
          <Input size="sm" type="text" placeholder={t('editor.comments.answer')} innerRef={messageRef} name="send-response" />
        </FormGroup>
        <Button type="button" color="success" size="sm" className="ml-1" data-action="send-message" onClick={handleSend}>
          <i className="fas fa-paper-plane"></i>
        </Button>
      </Form>
    </CommentItem>
  )
}

function CommentItem({ user, email, createdAt, message, isOwner, currentTab,  canDelete,  onDelete, children }) {
  const { t } = useTranslation();
  return (
    <div className="comment-item d-flex p-2">
       <div className="comment-user-image p-2">
          <img  className="rounded-circle" src={getPhoto(user)} style={{width:40, height:40}}  />
        </div>
        <div className="comment-user-content text-dark p-2 flex-column">
          
          <div className="comment-user d-flex justify-content-between align-items-center">
            {getUsernameOrEmail(user, email)}
            {isOwner && currentTab === 'collab' && canDelete &&
              <small className="comment-delete text-danger" onClick={onDelete}>{t('editor.comments.delete')}</small>
            }
          </div>
          <div className="mb-2">
            <small className="comment-datetime">{format(createdAt)}</small>
          </div>
          <div className="comment-message mb-1">
            {message}
          </div>
          {children}
        </div>
    </div>
  )
}